import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { ListEditDeleteActions } from '../../../components/admin/ListRowActions';
import '../quotes/quote-generator.css';
import { apiRequest } from '../../../api/client';

type ClientRow = {
    _id: string;
    firstName?: string;
    lastName?: string;
    buildingName?: string;
    address?: string;
    phone?: string;
    email?: string;
    corpNo?: string;
    representativeName?: string;
    updatedAt?: string;
};

function displayName(c: ClientRow): string {
    const n = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return n || '—';
}

export default function ClientsPage(): React.JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();
    const [query, setQuery] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [clients, setClients] = useState<ClientRow[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(query.trim()), 300);
        return () => window.clearTimeout(t);
    }, [query]);

    const load = useCallback(() => {
        const q = encodeURIComponent(debouncedQ);
        apiRequest<{ data: ClientRow[] }>(`/api/clients?q=${q}&limit=200`)
            .then((res) => {
                setErrorMessage(null);
                setClients(Array.isArray(res?.data) ? res.data : []);
            })
            .catch((err: unknown) => {
                setErrorMessage(err instanceof Error ? err.message : 'Failed to load clients.');
                setClients([]);
            });
    }, [debouncedQ]);

    useEffect(() => {
        load();
    }, [load, location.key]);

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '1000px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}
                    <header className="quote-header" style={{ marginBottom: '28px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px' }}>Clients</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '1.05rem' }}>
                                Search saved clients and add new ones for quotes and invoices.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn-primary"
                            style={{
                                padding: '12px 24px',
                                fontSize: '0.95rem',
                                boxShadow: '0 4px 14px rgba(46, 138, 87, 0.3)'
                            }}
                            onClick={() => navigate('/admin/clients/new')}
                        >
                            + Add client
                        </button>
                    </header>

                    <div className="quote-card" style={{ marginBottom: '20px', padding: '16px 18px' }}>
                        <label className="form-label" htmlFor="client-search">
                            Search
                        </label>
                        <input
                            id="client-search"
                            className="form-input"
                            placeholder="Name, building, address, or email"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            autoComplete="off"
                        />
                    </div>

                    <section>
                        <div
                            className="quote-section-title"
                            style={{ border: 'none', fontSize: '1.2rem', marginBottom: '14px' }}
                        >
                            Directory ({clients.length})
                        </div>
                        <div
                            className="quote-card clients-directory-card"
                            style={{ padding: '14px 18px', overflow: 'hidden' }}
                        >
                            {clients.length === 0 ? (
                                <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--dash-muted)' }}>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>No clients match your search yet.</p>
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        style={{ marginTop: '16px' }}
                                        onClick={() => navigate('/admin/clients/new')}
                                    >
                                        Add your first client
                                    </button>
                                </div>
                            ) : (
                                <div className="list-responsive">
                                    <div className="list-table-pane quote-table-container">
                                        <table className="invoice-table clients-directory-table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Building</th>
                                                    <th>Address</th>
                                                    <th>Email</th>
                                                    <th>Phone</th>
                                                    <th>Updated</th>
                                                    <th aria-label="Actions" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clients.map((c) => (
                                                    <tr
                                                        key={c._id}
                                                        role="button"
                                                        tabIndex={0}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => navigate(`/admin/clients/${c._id}`)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                navigate(`/admin/clients/${c._id}`);
                                                            }
                                                        }}
                                                    >
                                                        <td style={{ fontWeight: 600 }}>{displayName(c)}</td>
                                                        <td>{c.buildingName || '—'}</td>
                                                        <td>{c.address || '—'}</td>
                                                        <td>{c.email || '—'}</td>
                                                        <td>{c.phone || '—'}</td>
                                                        <td style={{ color: 'var(--dash-muted)' }}>
                                                            {c.updatedAt
                                                                ? new Date(c.updatedAt).toLocaleDateString()
                                                                : '—'}
                                                        </td>
                                                        <td
                                                            style={{ textAlign: 'right' }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <ListEditDeleteActions
                                                                onEdit={() => navigate(`/admin/clients/${c._id}`)}
                                                                editTitle="View client"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="list-cards-pane">
                                        {clients.map((c) => (
                                            <article
                                                key={`m-${c._id}`}
                                                role="button"
                                                tabIndex={0}
                                                className="list-land-card list-land-card--clickable"
                                                onClick={() => navigate(`/admin/clients/${c._id}`)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        navigate(`/admin/clients/${c._id}`);
                                                    }
                                                }}
                                            >
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Name</span>
                                                    <span className="list-land-value">{displayName(c)}</span>
                                                </div>
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Building</span>
                                                    <span className="list-land-value">{c.buildingName || '—'}</span>
                                                </div>
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Phone</span>
                                                    <span className="list-land-value">{c.phone || '—'}</span>
                                                </div>
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Address</span>
                                                    <span className="list-land-value">{c.address || '—'}</span>
                                                </div>
                                                <div className="list-land-field list-land-field--span">
                                                    <span className="list-land-label">Email</span>
                                                    <span className="list-land-value">{c.email || '—'}</span>
                                                </div>
                                                <div className="list-land-field">
                                                    <span className="list-land-label">Updated</span>
                                                    <span className="list-land-value list-land-value--muted">
                                                        {c.updatedAt
                                                            ? new Date(c.updatedAt).toLocaleDateString()
                                                            : '—'}
                                                    </span>
                                                </div>
                                                <div className="list-land-actions">
                                                    <ListEditDeleteActions
                                                        onEdit={() => navigate(`/admin/clients/${c._id}`)}
                                                        editTitle="View client"
                                                    />
                                                </div>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <div style={{ marginTop: '32px', textAlign: 'center' }}>
                        <button
                            type="button"
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/admin/dashboard')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
